import { useEffect, useRef, useState } from "react";

/*----------------------------------------------------------------
  Generic column-resize hook.
----------------------------------------------------------------*/
export function useColumnResize(
  columns: string[],
  tableRef: React.RefObject<HTMLDivElement>,
  defaultWidth = 200
) {
  type Widths = Record<string, number>;

  /** Width map keyed by column name (initialised from props). */
  const [columnWidths, setColumnWidths] = useState<Widths>(() =>
    Object.fromEntries(columns.map((c) => [c, defaultWidth]))
  );

  /** ⚡️  Reset the width map whenever the _set_ of columns changes. */
  useEffect(() => {
    setColumnWidths(
      Object.fromEntries(columns.map((c) => [c, defaultWidth])) as Widths
    );
  }, [columns, defaultWidth]);

  /** Public helper to reset via UI button. */
  function resetColumnWidths() {
    setColumnWidths(
      Object.fromEntries(columns.map((c) => [c, defaultWidth])) as Widths
    );
  }

  // ----------------------------------------------------------------
  // drag-resize plumbing
  // ----------------------------------------------------------------
  const resizingColumn = useRef<string | null>(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  function handleResizeStart(col: string, e: React.MouseEvent) {
    e.preventDefault();
    resizingColumn.current = col;
    startX.current = e.clientX;
    startWidth.current = columnWidths[col] ?? defaultWidth;

    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
    document.body.classList.add("column-resizing");
  }

  function handleResizeMove(e: MouseEvent) {
    if (!resizingColumn.current) return;
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(100, startWidth.current + diff);
    setColumnWidths((prev) => ({
      ...prev,
      [resizingColumn.current!]: newWidth,
    }));
  }

  function handleResizeEnd() {
    resizingColumn.current = null;
    document.removeEventListener("mousemove", handleResizeMove);
    document.removeEventListener("mouseup", handleResizeEnd);
    document.body.classList.remove("column-resizing");
  }

  useEffect(
    () => () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    },
    []
  );

  return { columnWidths, resetColumnWidths, handleResizeStart };
}
